import type { DependencyObject } from './deviceApi'

/**
 * Normalized API error shape produced by the api layer's error mapping
 * (see `mapApiError` in axiosInstance). Lives here — a leaf with no imports — so
 * the transport can reference it without importing the stores layer, which would
 * re-create the api ⇄ store import cycles. Re-exported from `@/stores/auth` for
 * backward compatibility with existing importers.
 */
export interface ApiError {
	message: string
	status: number
	code?: string
	dependencies?: DependencyObject
}

/**
 * Extract the backend dependency object from an error object.
 */
export function getApiErrorDependencies(error: unknown): DependencyObject | null {
	if (!error) {
		return null
	}
	if (typeof error === 'object') {
		const errObj = error as Record<string, unknown>

		if ('dependencies' in errObj && errObj.dependencies && typeof errObj.dependencies === 'object') {
			return errObj.dependencies as DependencyObject
		}

		if (errObj.response && typeof errObj.response === 'object') {
			const respObj = errObj.response as Record<string, unknown>
			if (respObj.data && typeof respObj.data === 'object') {
				const dataObj = respObj.data as Record<string, unknown>
				if (dataObj.dependencies && typeof dataObj.dependencies === 'object') {
					return dataObj.dependencies as DependencyObject
				}
			}
		}
	}
	return null
}

/**
 * Extract the backend error reason from an error object (or string).
 */
export function getApiErrorMessage(error: unknown): string | null {
	if (!error) {
		return null
	}
	if (typeof error === 'string') {
		const trimmed = error.trim()
		return trimmed.length > 0 ? trimmed : null
	}
	if (typeof error === 'object') {
		const errObj = error as Record<string, unknown>

		// 1. Inspect response.data inside HTTP/Axios response objects first
		if (errObj.response && typeof errObj.response === 'object') {
			const respObj = errObj.response as Record<string, unknown>
			if (typeof respObj.data === 'string' && respObj.data.trim() && !respObj.data.trim().startsWith('<')) {
				return respObj.data.trim()
			}
			if (respObj.data && typeof respObj.data === 'object') {
				const dataObj = respObj.data as Record<string, unknown>
				if (typeof dataObj.error_message === 'string' && dataObj.error_message.trim()) {
					return dataObj.error_message.trim()
				}
				if (typeof dataObj.message === 'string' && dataObj.message.trim()) {
					return dataObj.message.trim()
				}
				if (typeof dataObj.error === 'string' && dataObj.error.trim()) {
					return dataObj.error.trim()
				}
				if (typeof dataObj.detail === 'string' && dataObj.detail.trim()) {
					return dataObj.detail.trim()
				}
			}
		}

		// 2. Inspect top-level error_message / error fields
		if (typeof errObj.error_message === 'string' && errObj.error_message.trim()) {
			return errObj.error_message.trim()
		}
		if (typeof errObj.error === 'string' && errObj.error.trim()) {
			return errObj.error.trim()
		}

		// 3. Inspect message field, skipping generic Axios transport messages ("Request failed with status code 409")
		if (typeof errObj.message === 'string' && errObj.message.trim()) {
			const msg = errObj.message.trim()
			if (!/^Request failed with status code \d+$/i.test(msg)) {
				return msg
			}
		}
	}
	return null
}

/**
 * Combines a user-facing context description (e.g. "Failed to upload firmware")
 * with the specific error reason sent by the backend (e.g. "conflict").
 *
 * Example: formatApiErrorMessage("Failed to upload firmware", error)
 * -> "Failed to upload firmware: conflict"
 */
export function formatApiErrorMessage(baseMessage: string, error?: unknown): string {
	const status = (error as { status?: unknown } | null | undefined)?.status
	const reason = typeof status === 'number' && status >= 500 ? null : getApiErrorMessage(error)
	if (reason && reason !== baseMessage && !baseMessage.toLowerCase().includes(reason.toLowerCase())) {
		return `${baseMessage}: ${reason}`
	}
	return baseMessage
}
